/**
 * App-level metrics tracker.
 *
 * Rendered only after login. Sends lightweight owner metrics without blocking UI.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { dataEvents } from '@services/dataEvents';
import {
  getLocalMetricCounts,
  metricCountsHash,
  normalizeRoute,
  trackMetricEvent,
} from '@services/metrics';

const HEARTBEAT_MS = 60_000;
const SETUP_SNAPSHOT_DEBOUNCE_MS = 2500;

export function MetricsTracker() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const hasStartedSessionRef = useRef(false);
  const currentRouteRef = useRef(normalizeRoute(location.pathname, location.search));
  const lastSetupHashRef = useRef<string | null>(null);
  const setupTimerRef = useRef<number | null>(null);

  useEffect(() => {
    currentRouteRef.current = normalizeRoute(location.pathname, location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasStartedSessionRef.current = false;
      lastSetupHashRef.current = null;
      return;
    }

    if (hasStartedSessionRef.current) return;
    hasStartedSessionRef.current = true;
    void trackMetricEvent('session_start', currentRouteRef.current);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const route = normalizeRoute(location.pathname, location.search);
    void trackMetricEvent('page_view', route);
  }, [isAuthenticated, location.pathname, location.search]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const heartbeat = window.setInterval(() => {
      void trackMetricEvent('heartbeat', currentRouteRef.current);
    }, HEARTBEAT_MS);

    return () => window.clearInterval(heartbeat);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const sendSetupSnapshot = () => {
      if (setupTimerRef.current) {
        window.clearTimeout(setupTimerRef.current);
      }

      setupTimerRef.current = window.setTimeout(async () => {
        const counts = await getLocalMetricCounts();
        const hash = metricCountsHash(counts);
        if (hash === lastSetupHashRef.current) return;

        lastSetupHashRef.current = hash;
        await trackMetricEvent('setup_snapshot', currentRouteRef.current, { counts });
      }, SETUP_SNAPSHOT_DEBOUNCE_MS);
    };

    void (async () => {
      const counts = await getLocalMetricCounts();
      lastSetupHashRef.current = metricCountsHash(counts);
    })();

    const unsubscribe = dataEvents.on('all', sendSetupSnapshot);

    return () => {
      unsubscribe();
      if (setupTimerRef.current) {
        window.clearTimeout(setupTimerRef.current);
      }
    };
  }, [isAuthenticated]);

  return null;
}

export default MetricsTracker;
